import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Alert, Button, Modal, Badge } from 'antd'
import type { SearchProps } from 'antd/es/input'

import { Container } from '../../components/HOC/container'
import { BackButton } from '../../components/BackButton'
import { useTranslation } from '../../shared/hooks/useTranslation'
import { isRepeatService } from '../../shared/serviceFilters'
import api from '../../app/api'

import style from './Service.module.scss'

const { Search, TextArea } = Input

type AiRecommendation = {
  usluga_name: string
  podrazdelenie_name: string
  reason: string
}

type AiResponse = {
  recommendations: AiRecommendation[]
  urgent: boolean
  urgent_message?: string
}

const MAX_SYMPTOMS_LENGTH = 500

type ServiceItem = {
  id: number
  usluga_name: string
  price: number
  doctor_count: number
  doctor_ids: string
  usluga_ids?: string
  podrazdelenie_name?: string
  podrazdelenie_id?: string
}

type GroupedDepartmentItem = {
  id: number
  podrazdelenie_name: string
  price: number
  doctor_count: number
  doctor_ids: string
  // Языконезависимые id услуг, входящих в этот отдел. По ним страница врачей
  // ищет врачей, не завися от названия/языка (см. Doctor.tsx).
  usluga_ids: string[]
  // ID отделений (podrazdelenie_id), чтобы на странице врачей не смешивать
  // отделения с одинаковыми usluga_id (детский/взрослый хирург и т.п.).
  podrazdelenie_ids: string[]
  service_variants: string[]
}

const SERVICE_SHOW_BRACKETS_KEY = 'service_show_brackets'

function stripBracketText(name: string): string {
  return name
    .replace(/\s*\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeDepartmentName(name: string, showBrackets: boolean): string {
  const sourceName = showBrackets ? name : stripBracketText(name)
  return sourceName
    .replace(/ /g, ' ')
    .toLowerCase()
    .replace(/[.,;:()"'`]/g, ' ')
    .replace(/[—–-]/g, ' ')
    .replace(/[\\/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function splitIds(raw?: string): string[] {
  return (raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function groupByDepartment(items: ServiceItem[], showBrackets: boolean): GroupedDepartmentItem[] {
  const grouped = new Map<string, GroupedDepartmentItem>()

  items.forEach((item) => {
    const rawDepartmentName = item.podrazdelenie_name?.trim() || item.usluga_name
    const displayDepartmentName = showBrackets
      ? rawDepartmentName
      : stripBracketText(rawDepartmentName) || rawDepartmentName
    const key = normalizeDepartmentName(rawDepartmentName, showBrackets)
    const existing = grouped.get(key)

    if (!existing) {
      grouped.set(key, {
        id: item.id,
        podrazdelenie_name: displayDepartmentName,
        price: item.price,
        doctor_count: item.doctor_count,
        doctor_ids: item.doctor_ids,
        usluga_ids: splitIds(item.usluga_ids),
        podrazdelenie_ids: item.podrazdelenie_id ? [item.podrazdelenie_id] : [],
        service_variants: [item.usluga_name],
      })
      return
    }

    if (!existing.service_variants.includes(item.usluga_name)) {
      existing.service_variants.push(item.usluga_name)
    }

    // Объединяем usluga_id всех вариантов отдела — врачи ищутся по всему набору.
    splitIds(item.usluga_ids).forEach((id) => {
      if (!existing.usluga_ids.includes(id)) {
        existing.usluga_ids.push(id)
      }
    })

    if (item.podrazdelenie_id && !existing.podrazdelenie_ids.includes(item.podrazdelenie_id)) {
      existing.podrazdelenie_ids.push(item.podrazdelenie_id)
    }

    if (item.price > 0 && (existing.price <= 0 || item.price < existing.price)) {
      existing.price = item.price
      existing.id = item.id
      existing.doctor_count = item.doctor_count
      existing.doctor_ids = item.doctor_ids
    }
  })

  return Array.from(grouped.values()).sort((a, b) =>
    a.podrazdelenie_name.localeCompare(b.podrazdelenie_name, 'ru'),
  )
}



export function Service() {
  const navigate = useNavigate()
  const { t, language } = useTranslation()
  const [services, setServices] = useState<GroupedDepartmentItem[]>([])
  const [filtered, setFiltered] = useState<GroupedDepartmentItem[]>([])
  const [show, setShow] = useState<number>(1)
  const [showBrackets, setShowBrackets] = useState(false)
  const [symptoms, setSymptoms] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiHints, setAiHints] = useState<Map<string, string>>(new Map())
  const [aiUrgentMessage, setAiUrgentMessage] = useState<string | null>(null)
  const [aiNoMatch, setAiNoMatch] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiAvailable, setAiAvailable] = useState(true)
  const [aiModalOpen, setAiModalOpen] = useState(false)

  const handleChangeService = (service: GroupedDepartmentItem) => () => {
    // Название и варианты услуги кладём в sessionStorage, а в URL оставляем
    // только короткий id (/service/123) — без токена и длинных названий.
    // Токен берётся из localStorage на странице врачей.
    sessionStorage.setItem(
      `svc:${service.id}`,
      JSON.stringify({
        name: service.podrazdelenie_name,
        variants: service.service_variants,
        uslugaIds: service.usluga_ids,
        podrazdelenieIds: service.podrazdelenie_ids,
      }),
    )
    navigate(`/service/${service.id}`)
  }

  const findGroupForRecommendation = (rec: AiRecommendation): GroupedDepartmentItem | undefined => {
    // 1) Точное совпадение по названию варианта услуги.
    const byVariant = services.find((s) => s.service_variants.includes(rec.usluga_name))
    if (byVariant) return byVariant

    // 2) Совпадение по НОРМАЛИЗОВАННОМУ названию услуги — на случай различий в
    //    скобках/регистре/пунктуации между ответом AI и списком на сайте.
    const recService = normalizeDepartmentName(rec.usluga_name || '', showBrackets)
    if (recService) {
      const byVariantNorm = services.find((s) =>
        s.service_variants.some((v) => normalizeDepartmentName(v, showBrackets) === recService),
      )
      if (byVariantNorm) return byVariantNorm
    }

    // 3) Совпадение по НОРМАЛИЗОВАННОМУ названию отделения (той же функцией, что
    //    и группировка списка) — раньше тут было строгое сравнение строк, и
    //    рекомендация терялась из-за вырезанных скобок/иной пунктуации.
    const targetDept = normalizeDepartmentName(rec.podrazdelenie_name || '', showBrackets)
    if (!targetDept) return undefined
    return services.find(
      (s) => normalizeDepartmentName(s.podrazdelenie_name, showBrackets) === targetDept,
    )
  }

  const resetAiHints = () => {
    setAiHints(new Map())
    setAiUrgentMessage(null)
    setAiNoMatch(false)
    setAiError(null)
  }

  const handleAskAi = async () => {
    const trimmed = symptoms.trim()
    if (!trimmed || aiLoading) return

    const token = localStorage.getItem('token') || ''
    if (!token) {
      setAiError(t.aiHelperError)
      return
    }

    setAiLoading(true)
    setAiError(null)
    setAiUrgentMessage(null)
    setAiNoMatch(false)

    try {
      const { data } = await api.post<AiResponse>('/services/recommend', {
        symptoms: trimmed,
        lang: language,
        token,
      })

      const hints = new Map<string, string>()
      for (const rec of data.recommendations) {
        const group = findGroupForRecommendation(rec)
        if (group && !hints.has(group.podrazdelenie_name)) {
          hints.set(group.podrazdelenie_name, rec.reason || '')
        }
      }
      setAiHints(hints)
      setAiNoMatch(hints.size === 0)
      if (data.urgent && data.urgent_message) {
        setAiUrgentMessage(data.urgent_message)
      }
      if (hints.size > 0) {
        setAiModalOpen(false)
      }
    } catch (err: any) {
      setAiHints(new Map())
      setAiNoMatch(false)
      const status = err?.response?.status
      if (status === 503) {
        setAiAvailable(false)
        setAiError(t.aiHelperUnavailable)
      } else if (status === 502) {
        setAiError(t.aiHelperUnavailable)
      } else {
        setAiError(t.aiHelperError)
      }
    } finally {
      setAiLoading(false)
    }
  }

  const onSearch: SearchProps['onSearch'] = (value) => {
    let result = services
    result = result.filter(s =>
      s.podrazdelenie_name.toLowerCase().includes(value.toLowerCase())
    )
    setFiltered(result)
  }

  useEffect(() => {
    setShowBrackets(localStorage.getItem(SERVICE_SHOW_BRACKETS_KEY) === '1')
  }, [])

  useEffect(() => {
    async function fetchServices() {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          return
        }

        const servicesRes = await api.get(`/services/available?token=${token}&lang=${language}`)
        const rawServices: ServiceItem[] = servicesRes.data || []
        const allServices = rawServices
          .filter((s) => (s?.usluga_name || '').trim().length > 0)
          .filter((s) => !isRepeatService(s.usluga_name))

        const sourceData = show === 1
          ? allServices.filter(s => s.price > 0)
          : allServices.filter(s => s.price === 0)
        const filteredData = groupByDepartment(sourceData, showBrackets)

        setServices(filteredData)
        setFiltered(filteredData)
      } catch (error) {

      }
    }

    fetchServices()
  }, [show, language, showBrackets])

  const orderedFiltered = useMemo(() => {
    if (aiHints.size === 0) return filtered
    const recommended: GroupedDepartmentItem[] = []
    const rest: GroupedDepartmentItem[] = []
    filtered.forEach((s) => {
      if (aiHints.has(s.podrazdelenie_name)) recommended.push(s)
      else rest.push(s)
    })
    return [...recommended, ...rest]
  }, [filtered, aiHints])

  return (
    <Container>
      <div className={style.column}>
        <div className={style.header}>
          <BackButton to="/" className={style.back} />
          <p className={style.paymentMethodTitle}>{t.paymentMethodTitle}</p>
          {show === 1 && aiAvailable && (
            <Badge count={aiHints.size} offset={[-6, 6]} color="#7c4dff" className={style.aiFabBadge}>
              <button
                type="button"
                className={style.aiFab}
                onClick={() => setAiModalOpen(true)}
                aria-label={t.aiHelperTitle}
                title={t.aiHelperTitle}
              >
                <svg className={style.aiFabIcon} viewBox="0 0 32 32" fill="none" aria-hidden>
                  <defs>
                    <linearGradient id="aiFabGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#9c5cff" />
                      <stop offset="1" stopColor="#4dabff" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M16 4l2.6 6.8L25.4 13l-6.8 2.6L16 22.4 13.4 15.6 6.6 13l6.8-2.2L16 4z"
                    fill="url(#aiFabGrad)"
                  />
                  <circle cx="24" cy="24" r="2.4" fill="url(#aiFabGrad)" />
                  <circle cx="8" cy="24" r="1.6" fill="url(#aiFabGrad)" opacity="0.7" />
                </svg>
                <span className={style.aiFabLabel}>AI</span>
              </button>
            </Badge>
          )}
        </div>
        <div className={style.paymentToggle}>
          <button
            type="button"
            className={`${style.toggleItem} ${show === 1 ? style.toggleItemActive : ''}`}
            onClick={() => setShow(1)}
          >
            {t.paidServices}
          </button>
          <button
            type="button"
            className={`${style.toggleItem} ${show === 2 ? style.toggleItemActive : ''}`}
            onClick={() => setShow(2)}
          >
            {t.freeServices}
          </button>
        </div>

        {show === 2 && (
          <Alert
            message={t.osmsTitle}
            description={
              <div className={style.osmsInfo}>
                <p>{t.osmsMessage}</p>
                <div className={style.phoneNumbers}>
                  <a href="tel:+77750071520" className={style.phoneLink}>+7 (775) 007-15-20</a>
                  <a href="tel:+77750075020" className={style.phoneLink}>+7 (775) 007-50-20</a>
                  <a href="tel:+77750078494" className={style.phoneLink}>+7 (775) 007-84-94</a>
                </div>
              </div>
            }
            type="info"
            showIcon
            className={style.osmsAlert}
          />
        )}

        {show === 1 && (
          <>
            {aiUrgentMessage && (
              <Alert
                type="error"
                showIcon
                message={t.aiHelperUrgent}
                description={aiUrgentMessage}
                className={style.aiHelperAlert}
                style={{ marginBottom: 8 }}
              />
            )}

            <div className={style.searchSection}>
              <p className={style.instruction}><strong>{t.serviceInstruction}</strong></p>
              <Search
                placeholder={t.serviceSearchPlaceholder}
                onSearch={onSearch}
                className={style.search}
              />
            </div>
            <div className={style.services}>
              {orderedFiltered.map((service, index) => {
                const reason = aiHints.get(service.podrazdelenie_name)
                const isRecommended = reason !== undefined
                const buttonClass = isRecommended
                  ? `${style.button} ${style.recommendedButton}`
                  : style.button
                return (
                  <button
                    key={index}
                    className={buttonClass}
                    onClick={handleChangeService(service)}
                  >
                    <div className={style.buttonContent}>
                      {isRecommended && (
                        <span className={style.recommendedBadge}>
                          <span aria-hidden>✨</span> {t.aiHelperBadge}
                        </span>
                      )}
                      <p className={style.content}>{service.podrazdelenie_name}</p>
                      {isRecommended && reason && (
                        <p className={style.reasonText}>{reason}</p>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      <Modal
        open={aiModalOpen}
        onCancel={() => setAiModalOpen(false)}
        footer={null}
        destroyOnClose={false}
        centered
        title={
          <span className={style.aiHelperTitle} style={{ margin: 0 }}>
            <span className={style.aiHelperSparkle} aria-hidden>✨</span>
            {t.aiHelperTitle}
          </span>
        }
        width={520}
      >
        <div className={style.aiHelper} style={{ border: 'none', padding: 0 }}>
          <p className={style.aiHelperSubtitle}>{t.aiHelperSubtitle}</p>
          <p className={style.aiHelperDisclaimer}>{t.aiHelperDisclaimer}</p>

          <div className={style.aiHelperTextareaWrap}>
            <TextArea
              value={symptoms}
              onChange={(e) => setSymptoms(e.target.value)}
              placeholder={t.aiHelperPlaceholder}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={MAX_SYMPTOMS_LENGTH}
              disabled={aiLoading}
              className={style.aiHelperTextarea}
            />
            <span className={style.aiHelperCounter} aria-hidden>
              {symptoms.length}/{MAX_SYMPTOMS_LENGTH}
            </span>
          </div>

          <div className={style.aiHelperActions}>
            {aiHints.size > 0 && (
              <Button size="small" onClick={resetAiHints} disabled={aiLoading}>
                {t.aiHelperClear}
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleAskAi}
              loading={aiLoading}
              disabled={!symptoms.trim() || aiLoading}
              className={style.aiHelperSubmit}
            >
              {t.aiHelperButton}
            </Button>
          </div>

          {aiError && (
            <Alert
              type="warning"
              showIcon
              message={aiError}
              className={style.aiHelperAlert}
            />
          )}

          {aiNoMatch && !aiError && (
            <Alert
              type="info"
              showIcon
              message={t.aiHelperNoMatch}
              className={style.aiHelperAlert}
            />
          )}
        </div>
      </Modal>
    </Container>
  )
}
